import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { 
  Sparkles, 
  Send,
  Copy,
  RefreshCw,
  Download,
  Upload,
  Image,
  Video,
  FileText,
  Eye,
  Check,
  X,
  Palette,
  Type,
  Film,
  Loader2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/toast";
import { generateAIContent } from "@/lib/marketing-api";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AIAssistant() {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState("");
  const [contentType, setContentType] = useState("marketing");
  const [generatedContent, setGeneratedContent] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  
  // Media states
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedVideo, setUploadedVideo] = useState(null);
  const [additionalData, setAdditionalData] = useState("");
  
  // Refs for file inputs
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const contentTypes = [
    { value: "marketing", labelKey: "aiPage.contentTypes.marketing" },
    { value: "announcement", labelKey: "aiPage.contentTypes.announcement" },
    { value: "invitation", labelKey: "aiPage.contentTypes.invitation" },
    { value: "promotion", labelKey: "aiPage.contentTypes.promotion" },
    { value: "engagement", labelKey: "aiPage.contentTypes.engagement" },
  ];

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error(t('aiPage.enterDescription'));
      return;
    }
    
    setLoading(true);
    try {
      const response = await generateAIContent({
        prompt,
        content_type: contentType,
        language: "ar"
      });
      setGeneratedContent(response.data.content);
      setSuggestions(response.data.suggestions || []);
      toast.success(t('aiPage.contentGenerated'));
    } catch (error) {
      toast.error(t('aiPage.errorGenerating'));
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedContent);
    toast.success(t('aiPage.contentCopied'));
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 25 * 1024 * 1024) {
      toast.error(t('aiPage.imageSizeError'));
      return;
    }
    
    setUploadingImage(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API_URL}/api/files/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        setUploadedImage({
          id: response.data.file_id,
          url: `${API_URL}${response.data.url}`,
          name: response.data.filename,
          type: response.data.content_type
        });
        toast.success(t('aiPage.imageUploaded'));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('aiPage.uploadError'));
    } finally {
      setUploadingImage(false);
    }
  };

  // Handle video upload
  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 250 * 1024 * 1024) {
      toast.error(t('aiPage.videoSizeError'));
      return;
    }
    
    setUploadingVideo(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API_URL}/api/files/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        setUploadedVideo({
          id: response.data.file_id,
          url: `${API_URL}${response.data.url}`,
          name: response.data.filename,
          type: response.data.content_type
        });
        toast.success(t('aiPage.videoUploaded'));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('aiPage.uploadError'));
    } finally {
      setUploadingVideo(false);
    }
  };

  // Remove uploaded image
  const removeImage = async () => {
    if (uploadedImage?.id) {
      try {
        await axios.delete(`${API_URL}/api/files/${uploadedImage.id}`);
      } catch (error) {
        console.error("Failed to delete image:", error);
      }
    }
    setUploadedImage(null);
  };

  // Remove uploaded video
  const removeVideo = async () => {
    if (uploadedVideo?.id) {
      try {
        await axios.delete(`${API_URL}/api/files/${uploadedVideo.id}`);
      } catch (error) {
        console.error("Failed to delete video:", error);
      }
    }
    setUploadedVideo(null);
  };

  // Download content as file
  const handleDownloadContent = () => {
    const content = {
      type: contentType,
      text: generatedContent,
      image: uploadedImage?.name || null,
      video: uploadedVideo?.name || null,
      additionalData: additionalData,
      createdAt: new Date().toISOString()
    };
    
    const dataStr = JSON.stringify(content, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `marketing-content-${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(t('aiPage.contentDownloaded'));
  };

  const hasContent = generatedContent || uploadedImage || uploadedVideo;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="ai-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-foreground">{t('aiPage.title')}</h1>
          <p className="text-muted-foreground mt-1">{t('aiPage.subtitle')}</p>
        </div>
        {hasContent && (
          <Button 
            onClick={handleDownloadContent}
            className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white font-bold shadow-lg shadow-blue-500/30"
            data-testid="download-content-btn"
          >
            <Download className="w-5 h-5 me-2" />
            {t('aiPage.downloadContent')}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Input Section */}
        <Card data-testid="ai-input-section" className="xl:col-span-1">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-xl">
              <Sparkles className="w-6 h-6 text-primary" />
              {t('aiPage.createContent')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Content Type */}
            <div className="form-group">
              <Label htmlFor="contentType" className="text-base font-semibold">{t('aiPage.contentType')}</Label>
              <Select value={contentType} onValueChange={setContentType}>
                <SelectTrigger data-testid="content-type-select" className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {contentTypes.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {t(type.labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            <div className="form-group">
              <Label htmlFor="prompt" className="text-base font-semibold">{t('aiPage.description')}</Label>
              <Textarea
                id="prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={t('aiPage.descriptionPlaceholder')}
                rows={4}
                className="mt-2"
                data-testid="ai-prompt-input"
              />
            </div>

            {/* Generate Button */}
            <button 
              onClick={handleGenerate} 
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold py-4 text-lg shadow-lg shadow-purple-500/30 rounded-md disabled:opacity-50 text-center"
              data-testid="generate-btn"
            >
              {loading ? (
                <span className="inline-flex items-center gap-2">
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  {t('aiPage.generating')}
                </span>
              ) : (
                <span className="inline-flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  {t('aiPage.generateWithAI')}
                </span>
              )}
            </button>

            {/* Media Upload */}
            <div className="pt-4 border-t border-border">
              <Label className="text-base font-semibold mb-3 block">{t('aiPage.addSupportingMedia')}</Label>
              <div className="grid grid-cols-2 gap-3">
                {/* Image Upload */}
                <div>
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    className="w-full h-20 flex-col gap-2 border-dashed border-2 hover:border-primary hover:bg-primary/5"
                    onClick={() => imageInputRef.current?.click()}
                    data-testid="upload-image-btn"
                    disabled={uploadingImage}
                  >
                    {uploadingImage ? (
                      <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
                    ) : (
                      <Image className="w-6 h-6 text-emerald-500" />
                    )}
                    <span className="text-xs">{uploadingImage ? t('aiPage.uploading') : t('aiPage.uploadImage')}</span>
                  </Button>
                  {uploadedImage && (
                    <div className="mt-2 text-xs text-emerald-500 flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      {uploadedImage.name.slice(0, 15)}...
                      <button onClick={removeImage} className="mr-1 text-red-500 hover:text-red-400">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Video Upload */}
                <div>
                  <input
                    ref={videoInputRef}
                    type="file"
                    accept="video/*"
                    onChange={handleVideoUpload}
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    className="w-full h-20 flex-col gap-2 border-dashed border-2 hover:border-primary hover:bg-primary/5"
                    onClick={() => videoInputRef.current?.click()}
                    data-testid="upload-video-btn"
                    disabled={uploadingVideo}
                  >
                    {uploadingVideo ? (
                      <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
                    ) : (
                      <Video className="w-6 h-6 text-blue-500" />
                    )}
                    <span className="text-xs">{uploadingVideo ? t('aiPage.uploading') : t('aiPage.uploadVideo')}</span>
                  </Button>
                  {uploadedVideo && (
                    <div className="mt-2 text-xs text-blue-500 flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      {uploadedVideo.name.slice(0, 15)}...
                      <button onClick={removeVideo} className="mr-1 text-red-500 hover:text-red-400">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Additional Data */}
            <div className="form-group">
              <Label className="text-sm">{t('aiPage.additionalData')}</Label>
              <Textarea
                value={additionalData}
                onChange={(e) => setAdditionalData(e.target.value)}
                placeholder={t('aiPage.additionalDataPlaceholder')}
                rows={2}
                className="mt-2 text-sm"
              />
            </div>
          </CardContent>
        </Card>

        {/* Generated Content */}
        <Card className="xl:col-span-1" data-testid="ai-output-section">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Type className="w-5 h-5 text-primary" />
                {t('aiPage.generatedContent')}
              </CardTitle>
              {generatedContent && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleCopy}
                  data-testid="copy-content-btn"
                >
                  <Copy className="w-4 h-4 me-1" />
                  {t('aiPage.copy')}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {generatedContent ? (
              <div className="space-y-3">
                <div 
                  className="bg-muted p-3 rounded-lg whitespace-pre-wrap text-foreground text-sm max-h-[250px] overflow-y-auto"
                  data-testid="generated-content"
                >
                  {generatedContent}
                </div>
                
                {suggestions.length > 0 && (
                  <div className="pt-3 border-t border-border">
                    <h4 className="text-xs font-medium mb-2 text-muted-foreground">💡 {t('aiPage.suggestions')}</h4>
                    <ul className="space-y-1">
                      {suggestions.slice(0, 2).map((suggestion, index) => (
                        <li key={index} className="text-xs text-muted-foreground">
                          • {suggestion}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
                <Sparkles className="w-10 h-10 mb-3 opacity-50" />
                <p className="text-sm">{t('aiPage.contentWillAppear')}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Preview Section */}
        <Card className="xl:col-span-1 border-2 border-primary/30 bg-gradient-to-br from-primary/5 to-transparent" data-testid="preview-section">
          <CardHeader className="pb-4 bg-primary/10 rounded-t-lg">
            <CardTitle className="flex items-center gap-2 text-xl">
              <Eye className="w-6 h-6 text-primary" />
              {t('aiPage.finalPreview')}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {hasContent ? (
              <div className="space-y-4">
                {/* Image Preview */}
                <div className="rounded-lg overflow-hidden bg-muted/50 border border-border">
                  {uploadedImage ? (
                    <div className="relative">
                      <img 
                        src={uploadedImage.url} 
                        alt="Banner" 
                        className="w-full h-40 object-cover"
                      />
                      <Button
                        size="icon"
                        variant="destructive"
                        className="absolute top-2 left-2 h-6 w-6"
                        onClick={() => setUploadedImage(null)}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                        <span className="text-xs text-white flex items-center gap-1">
                          <Image className="w-3 h-3" />
                          {t('aiPage.bannerImage')}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="h-32 flex flex-col items-center justify-center text-muted-foreground">
                      <Palette className="w-8 h-8 mb-2 opacity-50" />
                      <span className="text-xs">{t('aiPage.noImageUploaded')}</span>
                    </div>
                  )}
                </div>

                {/* Video Preview */}
                <div className="rounded-lg overflow-hidden bg-muted/50 border border-border">
                  {uploadedVideo ? (
                    <div className="relative">
                      <video 
                        src={uploadedVideo.url}
                        className="w-full h-32 object-cover"
                        controls
                      />
                      <Button
                        size="icon"
                        variant="destructive"
                        className="absolute top-2 left-2 h-6 w-6"
                        onClick={() => setUploadedVideo(null)}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ) : (
                    <div className="h-24 flex flex-col items-center justify-center text-muted-foreground">
                      <Film className="w-8 h-8 mb-2 opacity-50" />
                      <span className="text-xs">{t('aiPage.noVideoUploaded')}</span>
                    </div>
                  )}
                </div>

                {/* Text Preview */}
                <div className="rounded-lg bg-muted/50 border border-border p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-primary" />
                    <span className="text-xs font-semibold">{t('aiPage.marketingText')}</span>
                  </div>
                  {generatedContent ? (
                    <p className="text-xs text-foreground whitespace-pre-wrap line-clamp-6">
                      {generatedContent}
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground italic">
                      {t('aiPage.noContentYet')}
                    </p>
                  )}
                </div>

                {/* Additional Data */}
                {additionalData && (
                  <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-4 h-4 text-amber-500" />
                      <span className="text-xs font-semibold text-amber-600">{t('aiPage.additionalData')}</span>
                    </div>
                    <p className="text-xs text-foreground">
                      {additionalData}
                    </p>
                  </div>
                )}

                {/* Download Button */}
                <Button 
                  onClick={handleDownloadContent}
                  className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold shadow-lg"
                  data-testid="download-final-btn"
                >
                  <Download className="w-4 h-4 me-2" />
                  {t('aiPage.downloadFinalContent')}
                </Button>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-80 text-muted-foreground">
                <Eye className="w-16 h-16 mb-4 opacity-30" />
                <p className="text-center text-sm whitespace-pre-line">
                  {t('aiPage.createContentAndMedia')}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Tips */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-6 justify-center text-sm">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-purple-500" />
              <span>{t('aiPage.poweredByGPT')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Image className="w-4 h-4 text-emerald-500" />
              <span>{t('aiPage.imageSupport')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Video className="w-4 h-4 text-blue-500" />
              <span>{t('aiPage.videoSupport')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Download className="w-4 h-4 text-amber-500" />
              <span>{t('aiPage.downloadFullContent')}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
